import * as React from 'react';
import { useState, useEffect } from 'react';

/** Material UI Packages. */
import {
    Box,
    Typography,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Switch,
    Accordion,
    AccordionSummary,
    AccordionDetails,
} from '@mui/material';

/** Material UI Icons. */
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

/** Redux Packages. */
import { useDispatch, useSelector } from 'react-redux';

/** Redux Reducers. */
import { setSnackbar } from '../../state/slices/snackBarSlice';

/** Auth0 authenticator Packages. */
import { useAuth0 } from '@auth0/auth0-react';

/** Internal Components. */
import { callAPI } from '../../api/useApiRequest';
import CircularLoading from '../../components/CircularLoading';

const USER_ADDON_ERRORS = {
    INACTIVE_MAPPING_MESSAGE: 'Addon is inactive and cannot be newly mapped',
    INACTIVE_MAPPING_CODE: 'ADDON_INACTIVE',
    INACTIVE_MAPPING_SNACKBAR: 'Inactive Add-on is centrally managed. New mapping is blocked.',
};

export default function AddOns(props) {
    /** Get props from Account.js. */
    const { acctRows, setAcctRows, selectedId } = props;

    /** Retrieve authentication details from Auth0. */
    const { user, getAccessTokenSilently } = useAuth0();

    /** Get the dispatch function from Redux to send actions to the store. */
    const dispatch = useDispatch();

    /** Get the add-ons list from the store. */
    const addOnsState = useSelector((state) => state.addOns);
    const addOnsList = Array.isArray(addOnsState) ? addOnsState : [];
    const [loading, setLoading] = useState({ post: false, delete: false });

    /** State for the checked addons. */
    const [checked, setChecked] = useState([]);

    const getApiErrorMessage = (error) =>
        error?.response?.data?.message || error?.response?.data?.error || error?.message || '';

    const getApiErrorCode = (errorMessage) => {
        const codeMatch = errorMessage.match(/CODE:\s*([A-Z0-9_]+)/);
        return codeMatch ? codeMatch[1] : null;
    };

    /** Handle to toggle a addon. */
    const handleToggle = (addon) => () => {
        const addonId = addon.id;
        const findItem = checked.find((item) => item.id === addonId);
        const selectedAccount = acctRows.find((item) => item.id === selectedId);

        if (findItem) {
            const method = findItem.checked ? 'delete' : 'post';
            const payload = findItem.checked
                ? {
                      id: addon.il_customer_addon_map_id,
                  }
                : {
                      il_customer_id: selectedAccount.id,
                      il_add_on_id: addonId,
                  };

            callAPI({
                method: method,
                module: 'customer_mapping',
                component: 'customer_add_ons',
                getAccessTokenSilently,
                email: user.email,
                obj: JSON.stringify(payload),
                onInit: () => {
                    setLoading((prev) => ({ ...prev, post: true }));
                },
                onSuccess: (resp) => {
                    // API 성공 후 로컬 상태 업데이트
                    setChecked((prev) =>
                        prev.map((item) => (item.id === addonId ? { ...item, checked: !item.checked } : item)),
                    );
                    handleUpdateAcctRows(method, resp.data);
                    dispatch(
                        setSnackbar({
                            open: true,
                            message: 'Add-on has been successfully updated.',
                            severity: 'success',
                        }),
                    );
                },
                onError: (error) => {
                    const errorMessage = getApiErrorMessage(error);
                    const errorCode = getApiErrorCode(errorMessage);
                    const inactiveMessage =
                        errorCode === USER_ADDON_ERRORS.INACTIVE_MAPPING_CODE ||
                        errorMessage.includes(USER_ADDON_ERRORS.INACTIVE_MAPPING_MESSAGE)
                            ? USER_ADDON_ERRORS.INACTIVE_MAPPING_SNACKBAR
                            : 'Something went wrong. Please try again.';
                    dispatch(
                        setSnackbar({
                            open: true,
                            message: inactiveMessage,
                            severity:
                                errorCode === USER_ADDON_ERRORS.INACTIVE_MAPPING_CODE ||
                                errorMessage.includes(USER_ADDON_ERRORS.INACTIVE_MAPPING_MESSAGE)
                                    ? 'warning'
                                    : 'error',
                        }),
                    );
                },
                onFinal: () => {
                    setLoading((prev) => ({ ...prev, post: false }));
                },
            });
        }
    };

    const handleUpdateAcctRows = (method, value) => {
        if (method === 'post') {
            /** e.g. value = {id: 1, il_trade_partner_id: 1, il_customer_id: 1} */
            setAcctRows(
                acctRows.map((item) =>
                    item.id === selectedId
                        ? { ...item, il_customer_addon_map: [...item.il_customer_addon_map, value] }
                        : item,
                ),
            );
        } else {
            /** e.g. value = 1 */
            setAcctRows(
                acctRows.map((item) =>
                    item.id === selectedId
                        ? {
                              ...item,
                              il_customer_addon_map: item.il_customer_addon_map.filter((item) => item.id !== value.id),
                          }
                        : item,
                ),
            );
        }
    };

    useEffect(() => {
        const selectedAccount = acctRows.find((item) => item.id === selectedId);

        setChecked(
            addOnsList.map((addon) => {
                // 매핑 정보 찾기
                const mapping = selectedAccount?.il_customer_addon_map?.find((item) => item.il_add_on_id === addon.id);

                return {
                    id: addon.id,
                    name: addon.name,
                    status: addon.status,
                    // Central master status is the source of truth for effective on/off in account view.
                    checked: !!mapping && !!addon.status,
                    il_customer_addon_map_id: mapping?.id || null, // 없으면 null
                };
            }),
        );
    }, [addOnsList, acctRows, selectedId]);

    if (loading.post || loading.delete) {
        return <CircularLoading loading={loading.post || loading.delete} />;
    }

    return (
        <Box sx={{ my: 2, mx: 5, height: '95%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                    sx={{
                        width: '3px',
                        height: '16px',
                        backgroundColor: 'primary.main',
                    }}
                />
                <Typography sx={{ fontWeight: 500, fontSize: 16, color: 'text.secondary' }}>Addons Setting</Typography>
            </Box>

            <Accordion defaultExpanded sx={{ width: '50%', mt: 2, borderRadius: '5px' }}>
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="addons-content"
                    id="addons-header"
                    sx={{ bgcolor: '#f1f2f6', borderRadius: '5px', my: 0 }}
                >
                    <Typography sx={{ fontWeight: 500, fontSize: 15, color: 'text.secondary' }}>
                        Smart System
                    </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 3 }}>
                    <List sx={{ width: '100%' }}>
                        {checked.map((addon) => (
                            <ListItem key={addon.id} sx={{ border: '1px solid #e0e0e0' }}>
                                <ListItemIcon>
                                    <Typography sx={{ fontSize: '16px', color: 'text.primary' }}>•</Typography>
                                </ListItemIcon>
                                <ListItemText
                                    id={`switch-list-label-${addon.name.toLowerCase()}`}
                                    primary={addon.name}
                                    secondary={
                                        !addon.status
                                            ? 'Inactive (centrally controlled): existing customer mappings are preserved and temporarily OFF.'
                                            : null
                                    }
                                    slotProps={{
                                        secondary: {
                                            sx: { color: 'warning.main', fontSize: 12 },
                                        },
                                    }}
                                />
                                <Switch
                                    edge="end"
                                    onChange={handleToggle(addon)}
                                    checked={addon.checked}
                                    disabled={!addon.status}
                                />
                            </ListItem>
                        ))}
                    </List>
                </AccordionDetails>
            </Accordion>
        </Box>
    );
}
